import apolloClient from 'api/apolloClient';
import {
  ARCHIVE_GROUP,
  CREATE_GROUP,
  GET_AGENTS_OF_RECORD,
  GET_ASSIGNEES_AND_AGENTS_OF_RECORDS,
  GET_GROUPS_BY_USER_ID,
  GET_GROUPS_PAGE,
  GET_SPONSORS_GROUPS_PAGE,
  GROUP_BY_ID,
  REMOVE_GROUP,
  SEARCH_GROUP_BY_NAME,
  SEND_RENEWAL_DIGEST,
  TRANSFER_GROUP,
  UNARCHIVE_GROUP,
  UPDATE_AOR,
  UPDATE_GROUP,
} from 'api/queries/groupService';
import { FetchResult } from 'apollo-link';
import {
  Group,
  GroupSearchReturnType,
  GroupSortBy,
  GroupsPaginationDTO,
  InputGroup,
  InputAssignee,
  SortOrder,
  GroupFilter,
  BiGroupsPaginationDTO,
  BiGroupPageParams,
  Assignee,
} from 'types/groupService';

interface GroupPageParams {
  sortBy: GroupSortBy;
  sortOrder: SortOrder;
  filter: GroupFilter;
  page: number;
  fullView: boolean;
}

export async function getGroupsPage({
  sortBy,
  sortOrder,
  filter,
  page,
  fullView,
}: GroupPageParams): Promise<GroupsPaginationDTO> {
  return (
    await apolloClient.query<{ getGroupsPage: GroupsPaginationDTO }>({
      query: GET_GROUPS_PAGE,
      variables: { sortBy, sortOrder, filter, page, fullView },
      fetchPolicy: 'no-cache',
    })
  ).data.getGroupsPage;
}

export async function getSponsorsGroupsPage({
  sortBy,
  sortOrder,
  filter,
  page,
  fullView,
  name,
  agencyId,
  effectiveDate,
  assignedGroups,
  createdByMe,
  state,
  carrierGroupNumber,
}: BiGroupPageParams): Promise<BiGroupsPaginationDTO> {
  return (
    await apolloClient.query<{ getSponsorGroupsPage: BiGroupsPaginationDTO }>({
      query: GET_SPONSORS_GROUPS_PAGE,
      variables: {
        sortBy,
        sortOrder,
        filter,
        page,
        fullView,
        name,
        agencyId,
        effectiveDate,
        assignedGroups,
        createdBy: createdByMe,
        state,
        carrierGroupNumber,
      },
      fetchPolicy: 'no-cache',
    })
  ).data.getSponsorGroupsPage;
}

export async function getGroupById(id: string): Promise<Group> {
  return (
    await apolloClient.query<{ groupById: Group }>({
      query: GROUP_BY_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    })
  ).data.groupById;
}

export async function createGroup(inputGroup: InputGroup): Promise<Group> {
  const res = await apolloClient.mutate<{ createGroup: Group }>({
    mutation: CREATE_GROUP,
    variables: { inputGroup },
  });

  return res.data!.createGroup;
}

export async function removeGroup(groupId: string): Promise<FetchResult<{ removeGroup: boolean }>> {
  return apolloClient.mutate<{ removeGroup: boolean }>({
    mutation: REMOVE_GROUP,
    variables: { groupId },
  });
}

export async function searchGroupsByName(
  groupName: string,
  limit = 10,
  fullView = false,
): Promise<GroupSearchReturnType[]> {
  return (
    await apolloClient.query<{ searchGroupByName: GroupSearchReturnType[] }>({
      query: SEARCH_GROUP_BY_NAME,
      variables: { groupName, limit, fullView },
    })
  ).data.searchGroupByName;
}

export async function archiveGroup(groupId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: ARCHIVE_GROUP,
    variables: { groupId },
  });
}

export async function unarchiveGroup(groupId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: UNARCHIVE_GROUP,
    variables: { groupId },
  });
}

export async function getGroupsByUserId(): Promise<Group[]> {
  return (
    await apolloClient.query<{ getGroupsByUserId: Group[] }>({
      query: GET_GROUPS_BY_USER_ID,
    })
  ).data.getGroupsByUserId;
}

export async function sendRenewalDigest(
  sponsorId: string,
  renewalEffectiveDate: string,
): Promise<FetchResult<{ sendRenewalDigest: boolean }>> {
  return apolloClient.mutate<{ sendRenewalDigest: boolean }>({
    mutation: SEND_RENEWAL_DIGEST,
    variables: { sponsorId, renewalEffectiveDate },
  });
}

export async function transferGroup(
  groupId: string,
  agencyId: string,
  agentNpn: string,
  agentTin?: string,
): Promise<FetchResult<{ transferGroup: boolean }>> {
  return apolloClient.mutate<{ transferGroup: boolean }>({
    mutation: TRANSFER_GROUP,
    variables: { groupId, agencyId, agentNpn, agentTin },
  });
}

export async function updateGroup(inputGroup: InputGroup): Promise<FetchResult<{ updateGroup: Group }>> {
  return apolloClient.mutate<{ updateGroup: Group }>({
    mutation: UPDATE_GROUP,
    variables: { inputGroup },
  });
}

export async function updateAOR(groupId: string, aor: InputAssignee): Promise<Group> {
  return (
    await apolloClient.mutate<{ updateAOR: Group }>({
      mutation: UPDATE_AOR,
      variables: { groupId, aor },
    })
  ).data!.updateAOR;
}

export async function requestAgentsOfRecord(): Promise<Assignee[]> {
  return (
    await apolloClient.query<{ getAgentsOfRecord: Assignee[] }>({
      query: GET_AGENTS_OF_RECORD,
    })
  ).data.getAgentsOfRecord;
}

export async function requestAgentsAndAssignees(
  agencyId?: string,
): Promise<{ agentsOfRecord: Assignee[]; assignees: Assignee[] }> {
  const res = await apolloClient.query<{ getAgentsOfRecord: Assignee[]; getAssignees: Assignee[] }>({
    query: GET_ASSIGNEES_AND_AGENTS_OF_RECORDS,
    fetchPolicy: 'no-cache',
    variables: { agencyId },
  });

  return { agentsOfRecord: res.data.getAgentsOfRecord, assignees: res.data.getAssignees };
}
